import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ArrowDownLeftIcon from '@atlaskit/icon/core/arrow-down-left';

import type { AtAGlanceSummaries } from '../../at-a-glance/buildAtAGlanceSummaries.ts';
import { ConnectionsByLineSection } from '../connections-by-line-section/ConnectionsByLineSection.tsx';

import type { AppearsInData } from './useConnectionSectionsData.ts';
import type { SectionExpansionProps } from './useSectionExpansion.ts';

const i18n = defineMessages({
	appearsIn: {
		id: 'byline-connections.preview-panel.section.appears-in',
		defaultMessage: 'Appears in',
		description:
			'Heading of the Teamwork Graph preview panel section listing the other pages and items that reference the current page. The section shows the number of references in a badge beside the heading.',
	},
	empty: {
		id: 'byline-connections.preview-panel.section.appears-in.empty',
		defaultMessage: 'This content is not referenced anywhere.',
		description:
			'Shown in the expanded "Appears in" section of the Teamwork Graph preview panel when no other pages or items reference the current page.',
	},
});

export type AppearsInSectionProps = AppearsInData &
	SectionExpansionProps & {
		/** cc-smarts one-liners keyed by ARI; a row renders the one for its link. */
		aiSummaries?: AtAGlanceSummaries;
		onPreviewOpen?: () => void;
		onAskRovo?: (prompt?: string) => void;
	};

/**
 * The panel's "Appears in" section: the entities that reference this content, as
 * `useConnectionSectionsData` fetched them from the Teamwork Graph.
 *
 * This is the only paged section, because it is the only one that reads from the
 * Teamwork Graph. The other sections have all of their items up front.
 */
export const AppearsInSection = ({
	links,
	loading,
	loadingMore,
	hasNextPage,
	endCursor,
	loadMore,
	error,
	aiSummaries,
	onPreviewOpen,
	onAskRovo,
	isExpanded,
	onToggleExpanded,
}: AppearsInSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	// The header count is pinned to the first page: the query returns no total, and even a
	// `count()` would not say how many of those rows ORS can resolve into rendered links.
	// `hasNextPage` adds the "+" instead.
	const initialCountRef = useRef<number | undefined>(undefined);
	const hasFailedWithoutLinks = Boolean(error) && links.length === 0;
	if (initialCountRef.current === undefined && !loading && !hasFailedWithoutLinks) {
		initialCountRef.current = links.length;
	}

	return (
		<ConnectionsByLineSection
			title={formatMessage(i18n.appearsIn)}
			iconBefore={<ArrowDownLeftIcon label="" spacing="none" />}
			items={links}
			linkDirection="incoming"
			aiSummaries={aiSummaries}
			onPreviewOpen={onPreviewOpen}
			onAskRovo={onAskRovo}
			isExpanded={isExpanded}
			onToggleExpanded={onToggleExpanded}
			// A failed query is not an empty graph, so it does not earn the explanation.
			emptyMessage={error ? undefined : formatMessage(i18n.empty)}
			count={initialCountRef.current ?? 0}
			hasMore={hasNextPage}
			pagination={{
				hasNextPage,
				endCursor,
				// `loading` also covers the initial catch-up paging, which `loadMore` refuses to interrupt.
				isLoading: loading || loadingMore,
				onLoadMore: loadMore,
			}}
			testId="teamwork-graph-preview-panel-appears-in-section"
		/>
	);
};
